import { Col, Modal, Row, Upload, UploadProps, Image, message } from "antd";

import { useIsMobile } from "../MobileBreakpointContext";
import { useQuote } from "../QuoteProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslationNamespace } from "../../../hooks/useTranslationNamespace";
import {
  LockFilled,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { RcFile, UploadFile } from "antd/es/upload";
import Card from "antd/es/card/Card";
import QuoteButton from "../components/QuoteButton/QuoteButton";
import {
  publicImagePath,
  uploadedImagePath,
} from "../../../utils/uploadedImage";
import PriceCapPanel from "../components/PriceCapPanel/PriceCapPanel";
import QuoteSummary from "../QuoteSummary";
import {
  trackQuotePhotoUploadStarted,
  trackQuotePhotoUploadCompleted,
  trackQuotePhotoUploadFailed,
  classifyUploadResponse,
  classifyUploadNetworkError,
  QuotePhotoUploadOutcome,
  QuoteUploadFailure,
} from "../../../utils/quoteAnalytics";
import { readUploadedFileKey } from "../../../utils/uploadResponse";
import "./scss/quote-step-photos.scss";
// quote-summary.css is still imported for the inline QuoteSummary block. (The
// welcome modal has no priced rendering left to style — see the photo-gate
// hardwire note below.)
import "../css/quote-summary.css";

const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const base64ToFile = async (data: string, fileName: string): Promise<File> => {
  const res: Response = await fetch(data);
  const blob: Blob = await res.blob();
  return new File([blob], fileName, { type: "image/jpeg" });
};

type StepPhotosParams = {
  next: () => void;
  isResumeMode?: boolean;
};

const StepPhotos = ({ next, isResumeMode }: StepPhotosParams) => {
  const quoteData = useQuote();
  const { t } = useTranslationNamespace("quote");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  // In-flight upload count for the desktop upload card's status line — antd's
  // beforeUpload fires handleUpload once per selected file, so this counts
  // the individual per-file fetches still outstanding (owner report
  // 2026-08-14: no in-flight indicator while multiple photos upload).
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const isMobile = useIsMobile();

  // One-shot guard — survives StrictMode's double-mount in dev.
  const arrivalEffectFiredRef = useRef(false);

  // On Photos arrival: (1) auto-open the photo-discount modal once per
  // browser session, (2) trigger the quote-ready email (server dedupes via
  // quoteReadyEmailSentAt). Both skipped in resume mode — the user came
  // back *from* the email, so the "check your inbox" copy reads wrong.
  useEffect(() => {
    if (arrivalEffectFiredRef.current) return;
    if (isResumeMode) return;

    const quoteId = quoteData.quoteId;
    if (!quoteId) return;

    // Latch only once we have a quoteId — otherwise the effect runs on
    // mount with quoteId=null (addQuote mutation still in flight),
    // returns early, and locks itself out of the re-run that fires when
    // quoteId actually arrives. Symptom: welcome email never sent.
    arrivalEffectFiredRef.current = true;

    const sessionKey = `photosModalShown:${quoteId}`;
    const alreadyShown =
      typeof window !== "undefined" &&
      window.sessionStorage?.getItem(sessionKey) === "1";

    if (!alreadyShown) {
      setWelcomeModalOpen(true);
      try {
        window.sessionStorage?.setItem(sessionKey, "1");
      } catch {
        // Private-browsing mode — modal still opens, just not deduped.
      }
    }

    quoteData.submitPersonalizedQuote({ sendSuccessEmail: true });
  }, [isResumeMode, quoteData.quoteId]);

  // Pre-warm the Book Now payment stack while the patient is on Photos, so the
  // inline Stripe Payment Element mounts instantly when they later tap Book
  // Now (mirrors the booking-calendar confirm-step pre-load). Two best-effort
  // background fetches:
  //   1. the lazy BookingPaymentPanel chunk (carries @stripe/stripe-js, kept
  //      OUT of the funnel main.js by the bundle guardrail — see BookNowButton);
  //   2. the Stripe.js SDK (loadStripe injects js.stripe.com/v3 and memoizes
  //      by key, so the panel's later loadStripe hits a warm cache).
  // Dynamic imports keep @stripe/stripe-js out of the Photos chunk too; a
  // failed pre-warm just falls back to loading at click time as before.
  const stripePrewarmedRef = useRef(false);
  useEffect(() => {
    if (stripePrewarmedRef.current) return;
    stripePrewarmedRef.current = true;
    let cancelled = false;
    // Same module BookNowButton lazy-loads — warms the shared chunk.
    import("../components/BookNowButton/BookingPaymentPanel").catch(() => {
      /* best-effort */
    });
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (key) {
      import("@stripe/stripe-js")
        .then(({ loadStripe }) => {
          if (!cancelled) void loadStripe(key);
        })
        .catch(() => {
          /* best-effort */
        });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // quote-cap-no-price-v1 A/B CONCLUDED 2026-07-28 — no_price won and now
  // serves 100% of over-cap traffic on this step too, so we no longer call
  // useABTestVariant. Over-cap unconditionally shows the compact PriceCapPanel;
  // the old control-arm "$15,000+" FinancingHero/plain-price rendering has been
  // deleted. See abTestConfigs.ts retired block + the ABTestResolution server
  // overlay.

  // photo-gated-price-v1 A/B CONCLUDED 2026-08-25 — "photo_gate" won and the
  // ESTIMATE-ONLY funnel now serves 100% of traffic, so we no longer call
  // useABTestVariant here (nothing is enrolled or stamped). No figure is ever
  // shown in the funnel; a coordinator sends the official estimate within 24h
  // (~1h once booked). The welcome modal's entire priced rendering is gone with
  // the control arm — the headline figure, the struck without-photos anchor, the
  // FinancingHero "as low as $X/mo" block and the display-cap machinery that
  // existed only to stop that figure leaking an over-cap number — leaving one
  // unconditional PriceCapPanel in its place. The pricing MODEL is untouched:
  // the 25% photo discount still applies in QuoteProvider, it is only never
  // rendered here. See abTestConfigs.ts retired block + the ABTestResolution
  // server overlay.
  //
  // Photo count only picks which COPY the panel shows — never whether the gated
  // branch applies. Keying anything on "no photos yet" would leak a priced
  // branch on the one path that reopens the modal with photos already in:
  // storage-blocked sessions, where the once-per-session dedupe silently no-ops
  // and a Back-navigation remount reopens it.
  const photoGatePhotosIn = quoteData.fileList.length > 0;

  const handleCancel = () => setPreviewOpen(false);

  const handlePreview = async (file: UploadFile) => {
    const base64content = await getBase64(file as RcFile);
    setPreviewImage(base64content);
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1),
    );
  };

  const handleChange: UploadProps["onChange"] = (info) => {};

  const handleUpload = async (filesToUpload: UploadFile[]) => {
    // NOTE: antd's beforeUpload below calls handleUpload([file]) once PER
    // FILE even on a multi-select (see the pendingUploadCount comment
    // above), so fileCount here is (almost) always 1 — these events are
    // per-file on the desktop surface, unlike QuoteSummaryTreatment.tsx's
    // handlePhotoUpload, which genuinely batches the whole selection. See
    // trackQuotePhotoUploadStarted's docstring.
    const fileCount = filesToUpload.length;
    const batchStartedAt = Date.now();
    trackQuotePhotoUploadStarted({ fileCount });

    setPendingUploadCount((c) => c + filesToUpload.length);
    // Analytics-only outcomes, one per file — collected independently of the
    // functional chain below so the completed/failed event can fire once
    // all of this call's fetches have settled.
    const uploadOutcomes: Promise<QuotePhotoUploadOutcome>[] = [];
    // Whether each file actually got stored into fileList — separate from
    // uploadOutcomes above (HTTP-status-only, for analytics) because a 200
    // response with a malformed body must still fail here: it gates both
    // storage and the failedCount driving the batch-end error message,
    // without disturbing the existing analytics bucket.
    const storedOutcomes: Promise<boolean>[] = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      const file: UploadFile = filesToUpload[i];

      const formData = new FormData();
      formData.append("file", file as RcFile);

      // Make the upload request
      const uploadRequest = fetch(
        process.env.PUBLIC_URL + "/s3/upload-s3-direct",
        {
          method: "POST",
          body: formData,
        },
      );

      // Analytics observer on the SAME fetch promise — reads only
      // response.ok/.status, never the body, so it can't race the
      // functional readUploadedFileKey read below.
      uploadOutcomes.push(
        uploadRequest
          .then((response) => classifyUploadResponse(response))
          .catch((error) => classifyUploadNetworkError(error)),
      );

      storedOutcomes.push(
        uploadRequest
          .then((response) => readUploadedFileKey(response))
          .then((key) => {
            // Generate a unique ID for the file
            const uid = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            const fileObject = {
              uid,
              response: {
                url: key,
              },
            };

            quoteData.setFileList((prevState: UploadFile[]) => [
              ...prevState,
              fileObject,
            ]);
            return true;
          })
          .catch((error) => {
            console.error("Upload error:", error);
            return false;
          })
          .finally(() => {
            setPendingUploadCount((c) => Math.max(0, c - 1));
          }),
      );
    }

    Promise.all(uploadOutcomes).then((outcomes) => {
      const durationMs = Date.now() - batchStartedAt;
      const failed = outcomes.find((outcome) => !outcome.ok) as
        | QuoteUploadFailure
        | undefined;
      if (failed) {
        trackQuotePhotoUploadFailed({
          fileCount,
          durationMs,
          errorBucket: failed.errorBucket,
        });
      } else {
        trackQuotePhotoUploadCompleted({ fileCount, durationMs });
      }
    });

    Promise.all(storedOutcomes).then((results) => {
      const failedCount = results.filter((stored) => !stored).length;
      if (failedCount === 0) return;
      // Always exactly one: antd's beforeUpload below calls handleUpload([file])
      // once PER FILE even on a multi-select, and that is this function's only
      // call site, so `results` has length 1. The uploadFailedPlural branch that
      // used to sit here could never run. (QuoteSummaryTreatment's
      // handlePhotoUpload DOES batch and keeps its own plural branch.)
      message.error(t("photos.uploadFailed", { count: failedCount }));
    });
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t("photos.uploadButton")}</div>
    </div>
  );

  return (
    <div className="quote-step-photos">
      <Modal
        open={welcomeModalOpen}
        onCancel={() => setWelcomeModalOpen(false)}
        footer={null}
        centered
        maskClosable={false}
        width={isMobile ? "92%" : 480}
      >
        <div style={{ padding: "12px 4px 0", textAlign: "center" }}>
          <div style={{ marginBottom: "20px", lineHeight: 1 }}>
            {/* Gated state: a $ coin above "your photos unlock your official
                estimate" copy undercuts the message — show a lock instead. */}
            <LockFilled style={{ fontSize: "84px", color: "#16a34a" }} />
          </div>

          <h2
            style={{
              fontSize: "30px",
              fontWeight: 700,
              color: "#111827",
              lineHeight: 1.2,
              marginTop: 0,
              marginBottom: "20px",
            }}
          >
            {t("photos.welcomeModal.titleGated")}
          </h2>

          <p
            style={{
              fontSize: "18px",
              color: "#374151",
              lineHeight: 1.55,
              marginBottom: "20px",
            }}
          >
            {t("photos.welcomeModal.bodyGated")}
          </p>

          <img
            src={publicImagePath("photo-examples.jpg")}
            alt=""
            style={{
              width: "100%",
              height: "auto",
              borderRadius: "8px",
              marginBottom: "20px",
            }}
          />

          {/* The ONLY thing that ever sits where the price used to: a copy panel,
              never a figure. Pre-photos it asks for them; post-photos it confirms
              the official estimate is on its way. */}
          <PriceCapPanel
            tone="photoGate"
            variant="compact"
            photosIn={photoGatePhotosIn}
            style={{ marginBottom: "28px" }}
          />

          <QuoteButton onClick={() => setWelcomeModalOpen(false)}>
            {t("photos.welcomeModal.ctaGated")}
          </QuoteButton>
        </div>
      </Modal>
      {isMobile && (
        <div className="step-photos__mobile-summary">
          <h2 className="step-photos__mobile-summary-title">
            {t("photos.yourQuote")}
          </h2>
          <QuoteSummary
            showPricing={true}
            showBookingCTA={true}
            showFileUploads={true}
          />
        </div>
      )}
      <Row>
        <Col
          span={isMobile ? 24 : 11}
          offset={!isMobile ? 1 : 0}
          className="step-photos__col"
        >
          {!isMobile && (
            <>
              <h1 className="step-photos__heading">
                <span>
                  {t("photos.headingPrefix")}{" "}
                  <span className="step-photos__heading-highlight">
                    {t("photos.headingHighlightGated")}
                  </span>
                </span>
                <span className="step-photos__required-chip">
                  {t("photos.required")}
                </span>
              </h1>
              <Card className="step-photos__upload-card">
                <Upload
                  className="quote-uploaded-photos"
                  listType="picture-card"
                  multiple
                  fileList={quoteData.fileList}
                  itemRender={(originNode, file) => (
                    <Image src={uploadedImagePath(file.response?.url)} />
                  )}
                  onPreview={handlePreview}
                  onChange={handleChange}
                  beforeUpload={(file: RcFile) => {
                    handleUpload([file]);
                    return false;
                  }}
                >
                  {quoteData.fileList.length <= 8 && uploadButton}
                </Upload>

                {/* At the 9-photo cap the add tile used to just vanish, which
                    reads as a broken control. Say why instead. */}
                {quoteData.fileList.length > 8 && (
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "13px",
                      color: "#6b7280",
                    }}
                  >
                    {t("photos.limitReached")}
                  </div>
                )}

                {pendingUploadCount > 0 && (
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#92400e",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <LoadingOutlined spin />
                    {pendingUploadCount === 1
                      ? t("photos.uploadingCount", { count: pendingUploadCount })
                      : t("photos.uploadingCountPlural", {
                          count: pendingUploadCount,
                        })}
                  </div>
                )}

                <Modal
                  open={previewOpen}
                  title={previewTitle}
                  footer={null}
                  onCancel={handleCancel}
                >
                  <img
                    alt="example"
                    style={{ width: "100%" }}
                    src={previewImage}
                  />
                </Modal>
                {quoteData.fileList.length === 0 && (
                  <img
                    className="step-photos__upload-example"
                    src={publicImagePath("photo-examples.jpg")}
                    alt=""
                  />
                )}
              </Card>
              <div
                style={{
                  marginTop: "12px",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 600,
                  textAlign: "center",
                  background:
                    quoteData.fileList.length > 0 ? "#ecfdf5" : "#fef3c7",
                  border: `1px solid ${
                    quoteData.fileList.length > 0 ? "#a7f3d0" : "#fde68a"
                  }`,
                  color:
                    quoteData.fileList.length > 0 ? "#065f46" : "#92400e",
                }}
              >
                {quoteData.fileList.length > 0
                  ? t("photos.estimateUnlocked")
                  : t("photos.unlockEstimate")}
              </div>
              <div className="quote-button-mobile-container">
                <QuoteButton
                  disabled={quoteData.fileList.length === 0}
                  onClick={next}
                >
                  {t("photos.consultationButton")}
                </QuoteButton>
              </div>
            </>
          )}
        </Col>
        <Col span={isMobile ? 24 : 11} className="step-photos__col">
          {!isMobile && (
            <QuoteSummary
              showPricing={true}
              showBookingCTA={true}
              showFileUploads={true}
            />
          )}
        </Col>
      </Row>

    </div>
  );
};

export default StepPhotos;
